import moment from 'moment';
import React, { useRef, useState } from 'react'
import { Badge, Button, Modal } from '@themesberg/react-bootstrap'
import { useCookies } from 'react-cookie';
import { cancelLotoCart, cancelLotoTicket, updateTicketImages } from '../../../../controller/api/__loto__/ticket'
import { updateCartStatus } from '../../../../controller/api/__loto__/cart'
import { getLotoTypeLabelWithFilter } from '../../../../helper/lotoType'
import TicketResultDetailModal from './TicketResultDetailModal'

const REGION_LABEL = {
  MB: 'Miền Bắc',
  MT: 'Miền Trung',
  MN: 'Miền Nam',
}

const STATUS_LABEL = {
  0: 'Chờ TT',
  1: 'Đang XL',
  2: 'Đang XL',
  3: 'Chờ KQ',
  4: 'Trúng thưởng',
  5: 'Không trúng',
  6: 'Đã huỷ - hoàn tiền',
}

const CANCELLABLE_STATUSES = [1, 2, 3]

const deriveCartStatus = (tickets) => {
  if (tickets.length > 0 && tickets.every(x => x.status === 6)) return 6
  if (tickets.length > 0 && tickets.every(x => x.status === 3)) return 3
  if (tickets.length > 0 && tickets.every(x => x.status === 4)) return 4
  if (tickets.length > 0 && tickets.every(x => x.status === 5)) return 5
  if (tickets.some(x => [1, 2].includes(x.status))) return 1
  return 0
}

const formatVnd = (value) => `${new Intl.NumberFormat('vn-VI', {}).format(Number(value || 0))}đ`

const getRewardPaymentText = (ticket) => {
  if (ticket.status !== 4) return ''
  if (ticket.rewardPaymentStatus === 'paid') return 'Đã trả thưởng'
  return 'Chờ trả thưởng'
}

const getTicketStatusBadge = (ticket) => {
  if (ticket.status === 4) {
    const isPaid = ticket.rewardPaymentStatus === 'paid'
    return (
      <>
        <Badge bg="danger" className="me-1">Trúng thưởng</Badge>
        <Badge bg={isPaid ? 'success' : 'warning'} text={isPaid ? undefined : 'dark'}>
          {isPaid ? 'Đã trả thưởng' : 'Chờ trả thưởng'}
        </Badge>
      </>
    )
  }

  if (ticket.status === 5) return <Badge bg="secondary">Không trúng</Badge>
  if (ticket.status === 6) return <Badge bg="danger">Đã huỷ - hoàn tiền</Badge>
  if (ticket.status === 3) return <Badge bg="primary">Chờ KQ</Badge>
  if (ticket.status === 1 || ticket.status === 2) return <Badge bg="warning" text="dark">Đang xử lý</Badge>
  return <Badge bg="secondary">{STATUS_LABEL[ticket.status] || ticket.status}</Badge>
}

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function TicketImageCell({ ticket, onImagesUpdated }) {
  const [cookies] = useCookies(['lottmekong'])
  const [images, setImages] = useState(ticket.images || [])
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef(null)

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files)
    if (!files.length) return

    setUploading(true)
    try {
      const base64List = await Promise.all(files.map(toBase64))
      // merge with existing: replace slots or append up to 2
      const merged = [...images]
      base64List.forEach((b64, i) => {
        merged[i] = b64
      })
      const capped = merged.slice(0, 2)

      const res = await updateTicketImages(cookies.lottmekong, ticket.id, capped)
      if (res && res.images) {
        setImages(res.images)
        onImagesUpdated && onImagesUpdated(ticket.id, res.images)
      }
    } catch (err) {
      console.error('upload error', err)
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  return (
    <td className="text-center" style={{ fontSize: 12 }}>
      <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
        {images.map((src, i) => src ? (
          <img
            key={i}
            alt={`Ảnh ${i + 1}`}
            src={src}
            height={80}
            style={{ cursor: 'pointer', borderRadius: 4, objectFit: 'cover' }}
            onClick={(e) => {
              e.stopPropagation()
            }}
          />
        ) : null)}
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <button
            type="button"
            className="btn btn-sm btn-dark"
            style={{ fontSize: 11, padding: '2px 8px' }}
            disabled={uploading}
            onClick={(e) => { e.stopPropagation(); fileRef.current && fileRef.current.click() }}
          >
            {uploading ? '...' : images.length > 0 ? 'Cập nhật' : '+ Ảnh'}
          </button>
        </div>
      </div>
    </td>
  )
}

export const TableRow = (props) => {
  const { user, createdAt, id, value, ticketCount, filterIsDone, activeTab, region } = props;
  const [tickets, setTickets] = useState(props.tickets || [])
  const [expanded, setExpanded] = useState(false)
  const [cartStatus, setCartStatus] = useState(() => {
    return deriveCartStatus(props.tickets || [])
  })
  const [updating, setUpdating] = useState(false)
  const [refundModalShow, setRefundModalShow] = useState(false)
  const [refundingCart, setRefundingCart] = useState(false)
  const [cancellingTicketId, setCancellingTicketId] = useState(null)
  const [cookies] = useCookies(['lottmekong'])
  const [detailTicketId, setDetailTicketId] = useState(null)

  const datedString = moment(createdAt || '').format('DD/MM/YYYY HH:mm').toLocaleString()
  const isNewTab = activeTab === 'new'
  const showImageColumns = isNewTab || filterIsDone === 1 || filterIsDone === -1
  const showStatusColumn = isNewTab || filterIsDone === -1
  const columnCount = 6 + (showStatusColumn ? 1 : 0) + (showImageColumns ? 1 : 0)
  const regionLabel = REGION_LABEL[region] || REGION_LABEL[tickets[0]?.region] || ''
  const pendingCount = tickets.filter(t => [1, 2].includes(t.status)).length
  const waitingResultCount = tickets.filter(t => t.status === 3).length
  const rewardCount = tickets.filter(t => t.status === 4).length
  const waitingRewardPaymentCount = tickets.filter(t => t.status === 4 && t.rewardPaymentStatus !== 'paid').length
  const paidRewardCount = tickets.filter(t => t.status === 4 && t.rewardPaymentStatus === 'paid').length
  const unRewardCount = tickets.filter(t => t.status === 5).length
  const cancelledCount = tickets.filter(t => t.status === 6).length
  const cancellableTickets = tickets.filter(t => CANCELLABLE_STATUSES.includes(t.status))
  const cartCanBeCancelled = cancellableTickets.length > 0 && tickets.every(
    ticket => CANCELLABLE_STATUSES.includes(ticket.status) || [6, 7, 8].includes(ticket.status)
  )
  const refundableCartValue = cancellableTickets.reduce(
    (total, ticket) => total + Number(ticket.value || 0),
    0
  )

  const handleImagesUpdated = (ticketId, newImages) => {
    setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, images: newImages } : t))
  }

  const handleStatusChange = async (e, newStatus) => {
    e.stopPropagation()
    if (updating) return
    setUpdating(true)
    try {
      const res = await updateCartStatus(cookies.lottmekong, id, newStatus)
      if (res && res.status === 'ok') {
        setCartStatus(newStatus)
        setTickets(prev => prev.map(t => ({ ...t, status: newStatus })))
      }
    } catch (err) {
      console.error(err)
    } finally {
      setUpdating(false)
    }
  }

  const updateRefundedTickets = (refundedTicketIds) => {
    setTickets(prev => {
      const next = prev.map(item => refundedTicketIds.includes(item.id)
        ? { ...item, status: 6, action: 'ADMIN_DELETE' }
        : item)
      setCartStatus(deriveCartStatus(next))
      return next
    })
  }

  const handleOpenCartRefund = (e) => {
    e.stopPropagation()
    if (!cartCanBeCancelled) return
    setRefundModalShow(true)
  }

  const handleConfirmCartRefund = async () => {
    if (refundingCart || !cartCanBeCancelled) return

    setRefundingCart(true)
    try {
      const res = await cancelLotoCart(cookies.lottmekong, id)

      if (!res || res.status !== 'success') {
        window.alert(res?.message || 'Không thể huỷ đơn và hoàn tiền. Vui lòng thử lại.')
        return
      }

      updateRefundedTickets(res.ticketIds || cancellableTickets.map(ticket => ticket.id))
      setRefundModalShow(false)
      window.alert(res.message || 'Đã huỷ đơn và hoàn tiền thành công')
    } catch (err) {
      console.error('cancel loto cart error', err)
      window.alert(err?.response?.data?.message || 'Không thể huỷ đơn và hoàn tiền. Vui lòng thử lại.')
    } finally {
      setRefundingCart(false)
    }
  }

  const handleCancelTicket = async (e, ticket) => {
    e.stopPropagation()
    if (cancellingTicketId) return

    const confirmed = window.confirm(
      `Bạn có chắc muốn huỷ vé ${ticket.id} và hoàn ${formatVnd(ticket.value)} vào ví người chơi?\n\nThao tác này không thể khôi phục.`
    )
    if (!confirmed) return

    setCancellingTicketId(ticket.id)
    try {
      const res = await cancelLotoTicket(cookies.lottmekong, ticket.id)
      if (!res || res.status !== 'success') {
        window.alert(res?.message || 'Không thể huỷ vé và hoàn tiền. Vui lòng thử lại.')
        return
      }

      updateRefundedTickets(res.ticketIds || [ticket.id])
      window.alert(res.message || 'Đã huỷ vé và hoàn tiền thành công')
    } catch (err) {
      console.error('cancel loto ticket error', err)
      window.alert(err?.response?.data?.message || 'Không thể huỷ vé và hoàn tiền. Vui lòng thử lại.')
    } finally {
      setCancellingTicketId(null)
    }
  }

  const statusButtons = () => {
    if (cartStatus === 0) return null
    if (cartStatus === 1 || cartStatus === 2) {
      return (
        <button
          className="btn btn-sm btn-dark"
          style={{ fontSize: 11, padding: '2px 10px', marginLeft: 6 }}
          disabled={updating}
          onClick={(e) => handleStatusChange(e, 3)}
        >
          {updating ? '...' : '✓ Xong'}
        </button>
      )
    }
    return null
  }

  const statusBadge = () => {
    const badges = []

    if (cartStatus === 0) {
      badges.push(<Badge key="unpaid" bg="secondary">Chưa TT</Badge>)
    }
    if (pendingCount > 0 || cartStatus === 1 || cartStatus === 2) {
      badges.push(<Badge key="processing" bg="warning" text="dark">Đang xử lý: {pendingCount || ticketCount}</Badge>)
    }
    if (waitingResultCount > 0) {
      badges.push(<Badge key="waiting-result" bg="primary">Chờ KQ: {waitingResultCount}</Badge>)
    }
    if (rewardCount > 0) {
      badges.push(<Badge key="reward" bg="danger">Trúng: {rewardCount}</Badge>)
    }
    if (waitingRewardPaymentCount > 0) {
      badges.push(<Badge key="waiting-payment" bg="warning" text="dark">Chờ trả thưởng: {waitingRewardPaymentCount}</Badge>)
    }
    if (paidRewardCount > 0) {
      badges.push(<Badge key="paid" bg="success">Đã trả thưởng: {paidRewardCount}</Badge>)
    }
    if (unRewardCount > 0) {
      badges.push(<Badge key="unreward" bg="secondary">Không trúng: {unRewardCount}</Badge>)
    }
    if (cancelledCount > 0) {
      badges.push(<Badge key="cancelled" bg="danger">Đã huỷ - hoàn tiền: {cancelledCount}</Badge>)
    }

    if (badges.length === 0) return null

    return (
      <div className="d-flex flex-wrap justify-content-center gap-1">
        {badges}
      </div>
    )
  }

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(prev => !prev)}>
        <td className="text-center">
          <span style={{ marginRight: 6, fontSize: 11 }}>{expanded ? '▲' : '▼'}</span>
          {id}
          {regionLabel ? <strong style={{ marginLeft: 8 }}>{regionLabel}</strong> : null}
        </td>
        <td className="text-center">
          {(user && user.name) || ""}<br />
          <span><strong className="text-primary">{(user && user.phone) || ""}</strong></span>
        </td>
        <td className="text-center">
          {formatVnd(value)}
        </td>
        <td className="text-center">
          {ticketCount} vé
        </td>
        {showStatusColumn && (
          <td className="text-center">
            {statusBadge()}
            {statusButtons()}
          </td>
        )}
        {showImageColumns && <td></td>}
        <td className="text-center">
          {cartCanBeCancelled ? (
            <button
              type="button"
              className="btn btn-sm btn-danger"
              style={{ fontSize: 11, padding: '4px 10px', whiteSpace: 'nowrap' }}
              disabled={refundingCart}
              onClick={handleOpenCartRefund}
            >
              Huỷ cả đơn & hoàn tiền
            </button>
          ) : '-'}
        </td>
        <td className="text-center">{datedString}</td>
      </tr>

      {expanded && (
        <tr style={{ backgroundColor: '#f5f5f5' }}>
          <td colSpan={columnCount} style={{ padding: 0 }}>
            <table className="table table-sm table-bordered mb-0">
              <thead className="text-center">
                <tr>
                  <th>Mã vé</th>
                  <th>Kỳ quay</th>
                  <th>Cách chơi</th>
                  <th>Số</th>
                  <th>Giá trị</th>
                  <th>Trạng thái</th>
                  <th>Tiền thưởng</th>
                  <th>Trả thưởng</th>
                  <th>Thao tác</th>
                  {showImageColumns && <th>Hình ảnh</th>}
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td className="text-muted text-center" style={{ fontSize: 12 }}>
                      {ticket.id}
                    </td>
                    <td className="text-center" style={{ fontSize: 12 }}>
                      {ticket.channel}<br />
                      <strong className="text-primary">{ticket.resultDate}</strong>
                    </td>
                    <td className="text-center" style={{ fontSize: 12 }}>
                      {getLotoTypeLabelWithFilter(ticket.lotoType, ticket.baoFilter, ticket.baoDigits)}
                    </td>
                    <td className="text-center" style={{ fontSize: 12 }}>
                      {ticket.numbers}
                    </td>
                    <td className="text-center" style={{ fontSize: 12 }}>
                      {formatVnd(ticket.value)}
                    </td>
                    <td className="text-center" style={{ fontSize: 12 }}>
                      {getTicketStatusBadge(ticket)}
                    </td>
                    <td className="text-center" style={{ fontSize: 12 }}>
                      {ticket.status === 4 ? (
                        <>
                          <strong className="text-danger">{formatVnd(ticket.rewardValue)}</strong>
                          {Number(ticket.rewardCount || 0) > 0 && (
                            <div className="text-muted">Trúng {ticket.rewardCount} lần</div>
                          )}
                        </>
                      ) : '-'}
                    </td>
                    <td className="text-center" style={{ fontSize: 12 }}>
                      {getRewardPaymentText(ticket) || '-'}
                    </td>
                    <td className="text-center" style={{ fontSize: 12 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-info"
                        style={{ fontSize: 11, padding: '2px 8px' }}
                        onClick={(e) => { e.stopPropagation(); setDetailTicketId(ticket.id) }}
                      >
                        Xem chi tiết
                      </button>
                      {CANCELLABLE_STATUSES.includes(ticket.status) && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger ms-1"
                          style={{ fontSize: 11, padding: '2px 8px' }}
                          disabled={cancellingTicketId === ticket.id}
                          onClick={(e) => handleCancelTicket(e, ticket)}
                        >
                          {cancellingTicketId === ticket.id ? 'Đang huỷ...' : 'Huỷ & hoàn tiền'}
                        </button>
                      )}
                    </td>
                    {showImageColumns && (
                      <TicketImageCell ticket={ticket} onImagesUpdated={handleImagesUpdated} />
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}

      <TicketResultDetailModal
        show={!!detailTicketId}
        ticketId={detailTicketId}
        onHide={() => setDetailTicketId(null)}
      />

      <Modal
        show={refundModalShow}
        onHide={() => { if (!refundingCart) setRefundModalShow(false) }}
        centered
      >
        <Modal.Header closeButton={!refundingCart}>
          <Modal.Title>Huỷ cả đơn & hoàn tiền</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning py-2">
            Toàn bộ vé trong đơn <strong>{id}</strong> sẽ bị huỷ và tiền được hoàn thẳng vào ví người chơi. Thao tác này không thể khôi phục.
          </div>
          <div className="border rounded p-3 text-center">
            <strong>{cancellableTickets.length} vé</strong>
            <span className="d-block text-danger fw-bold mt-1">Hoàn {formatVnd(refundableCartValue)}</span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={refundingCart}
            onClick={() => setRefundModalShow(false)}
          >
            Đóng
          </Button>
          <Button
            variant="danger"
            disabled={refundingCart || !cartCanBeCancelled}
            onClick={handleConfirmCartRefund}
          >
            {refundingCart ? 'Đang huỷ & hoàn tiền...' : 'Xác nhận huỷ cả đơn'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
